import { faListCheck, faPlus } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  BoxV4,
  ButtonV2,
  LabraToolTipV2,
  RadioButtonsV2,
} from '@labrav/react-components'
import { FieldArray, FormikProps, useFormikContext } from 'formik'
import {
  AzureOfferPlan,
  AzurePricingType,
} from '../../../../modules/privateOffer/types'
import { useStyles } from '../Plan/Plan.styles'
import { Product } from '../../../../modules/productsListing/types'
import { useCallback, useMemo } from 'react'
import { isEmpty } from 'lodash'
import clsx from 'clsx'
import {
  MicrosoftFormFAQs,
  MicrosoftFormFAQsItem,
} from '../MicrosoftFormFAQs/MicrosoftFormFAQs'
import { VmPlanRow } from './VmPlanRow'
import {
  isAzureVmProduct,
  resetVmPlansForPricingType,
  shouldDisableVmAbsolutePricing,
  vmEmptyPlan,
  vmPricingTypeMapping,
} from '../../../../components/MicrosoftFormComponent/VmPlan/vmPlanUtils'

export { isAzureVmProduct }

export const VmPlan = ({ product }: { product: Product }) => {
  const { classes } = useStyles()
  const { values, setFieldValue, errors, touched } =
    useFormikContext() as FormikProps<Record<string, unknown>>

  const plans = (values?.plans || []) as AzureOfferPlan[]
  const planIds = useMemo(
    () => plans.map(plan => plan?.labraPlanId || '').filter(id => !isEmpty(id)),
    [plans]
  )

  const disableAbsolutePricing = useMemo(
    () => shouldDisableVmAbsolutePricing({ plans, product }),
    [plans, product]
  )

  const radioButtonMappings = useMemo(
    () =>
      vmPricingTypeMapping.map(mapping => ({
        ...mapping,
        disabled:
          mapping.key === AzurePricingType.ABSOLUTE && disableAbsolutePricing,
        disabledTooltip:
          mapping.key === AzurePricingType.ABSOLUTE && disableAbsolutePricing
            ? 'Absolute is not applicable on the selected plan.'
            : '',
      })),
    [disableAbsolutePricing]
  )

  const handleSelection = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value

      if (value === AzurePricingType.ABSOLUTE && disableAbsolutePricing) {
        await setFieldValue(
          'azurePricingType',
          AzurePricingType.LABRA_PERCENTAGE
        )
        return
      }

      if (value) {
        if (value !== values.azurePricingType) {
          const updatedPlans = resetVmPlansForPricingType({
            plans,
            product,
          })
          await setFieldValue('plans', updatedPlans)
          await setFieldValue('azurePricingType', value)
        } else {
          await setFieldValue('azurePricingType', value)
        }
      } else {
        await setFieldValue('azurePricingType', values.azurePricingType)
      }
    },
    [
      plans,
      product,
      setFieldValue,
      values.azurePricingType,
      disableAbsolutePricing,
    ]
  )

  const isDisablePlanAdd = (product?.plans || [])?.length === plans?.length

  return (
    <BoxV4
      heading={'Pricing configuration'}
      headingIcon={
        <FontAwesomeIcon
          icon={faListCheck}
          style={{ width: '16px', height: '16px' }}
        />
      }
      secondaryActions={
        <MicrosoftFormFAQs item={MicrosoftFormFAQsItem.PRICING} />
      }
    >
      <div className={classes.sectionContainer}>
        <div className={clsx(classes.widthRadio, classes.radioSelection)}>
          <RadioButtonsV2
            name={'azurePricingType'}
            checkedValue={{
              key: (values.azurePricingType || '') as string,
              value: (values.azurePricingType || '') as string,
            }}
            handleChange={handleSelection}
            radioButtonMappings={radioButtonMappings}
          />

          {!!touched?.azurePricingType && errors?.azurePricingType && (
            <div className={classes.errorMessage}>
              {errors?.azurePricingType}
            </div>
          )}
        </div>

        <FieldArray name="plans">
          {({ push, remove }) => (
            <>
              {plans.map((planItem, index) => (
                <VmPlanRow
                  key={planItem?.labraPlanId || `vm-plan-${index}`}
                  plan={planItem}
                  remove={remove}
                  index={index}
                  product={product}
                  planIds={planIds}
                />
              ))}
              <LabraToolTipV2
                tooltipPlacement="top"
                description={
                  isDisablePlanAdd
                    ? 'All your existing plans have been added above.'
                    : ''
                }
              >
                <ButtonV2
                  className={classes.widthButton}
                  styleType="link"
                  onClick={() => {
                    push(vmEmptyPlan)
                  }}
                  data-testid="add-vm-plan-button"
                  disabled={isDisablePlanAdd}
                >
                  <FontAwesomeIcon
                    icon={faPlus}
                    width={'14px'}
                    height={'16px'}
                    style={{ paddingRight: '13px', paddingLeft: '5px' }}
                  />
                  Add plan
                </ButtonV2>
              </LabraToolTipV2>
            </>
          )}
        </FieldArray>
      </div>
    </BoxV4>
  )
}
